import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  ReactNode
} from "react";
import { withTheme } from "styled-components";
import Slider from "react-swipe";
import ChapterView from "./ChapterView";
import { withChapter, ChapterCtx } from "../ChapterContainer";
import { Theme } from "typedefs-ui/Theme";
import { useScriptureRouting } from "components/Bible/useScriptureRouting";
import { withBible, BibleCtx } from "components/Bible/BibleContainer";
import { difference } from "lodash-es";

interface SwipableChapterLayoutProps {
  children?: ReactNode;
  orderedChapterIdsString: string;
  scriptDirection: string;
  currentChapterId: string;
  hasChapter: boolean;
  setChapter: (params: { chapterId: string }) => void;
  setModeDefault: () => void;
}

// pads the current chapter with surrounding chapters
const getSurroundingChapterIds = ({
  chapterIdIndex,
  orderedChapterIds,
  buffer = 1
}: {
  chapterIdIndex: number;
  orderedChapterIds: string[];
  buffer: number;
}) => {
  return orderedChapterIds.slice(
    Math.max(chapterIdIndex - buffer, 0),
    chapterIdIndex + buffer + 1
  );
};

const BUFFER = 10;

// NOTES:
// we generate a list of BUFFER # of chapter ids before and after the current one
// using the current chapterId and the bibles orderedChapterIds
// e.g.: renderedChapterIds = [PSA.1, ..., PSA.10, current, PSA.12, ..., PSA.21]
//
// we then cache this list in state and render a ChapterView for each of them within the Slider
//
// when the user navigates forwards and backwards we check to make sure that it
// it still exists within the inner bounds of the renderedChapterIds:
// e.g.: [notGood, good, ..., good, ..., good, notGood]
//
// if the index is within a good range, we'll avoid updating the rendered chapters in anyway
// however, if we get to an index deemed outside of our good range, we'll update the renderedChapterIds.
// e.g.:
// before: renderedChapterIds = [PSA.1, ..., PSA.10, current, PSA.12, ..., PSA.21]
// 10 swipes forward: renderedChapterIds = [PSA.1, ..., PSA.20, current]
// will trigger: renderedChapterIds = [PSA.11, ..., PSA.20, current, PSA.22, ..., PSA.31]
// which will cause a complete rerender of the the Slider (causing the screen to flash)

const SwipableChapterLayout: React.FC<SwipableChapterLayoutProps> = ({
  orderedChapterIdsString,
  setChapter,
  currentChapterId,
  hasChapter,
  scriptDirection,
  setModeDefault,
  children
}) => {
  const orderedChapterIds = useMemo(
    () => orderedChapterIdsString.split(",").filter(x => x !== ""),
    [orderedChapterIdsString]
  );

  const chapterIdIndex = orderedChapterIds.indexOf(currentChapterId);

  const [renderedChapterIds, setRenderedChapterIds] = useState(
    getSurroundingChapterIds({
      chapterIdIndex,
      orderedChapterIds,
      buffer: BUFFER
    })
  );

  useEffect(() => {
    const newIndex = renderedChapterIds.indexOf(currentChapterId);

    // don't set new rendered chapterIds if we don't have a list of chapterIds to work with yet
    if (newIndex === -1 && orderedChapterIds.length === 0) return;
    if (newIndex <= 0 || newIndex >= renderedChapterIds.length - 1) {
      const newChapterIds = getSurroundingChapterIds({
        chapterIdIndex,
        orderedChapterIds,
        buffer: BUFFER
      });

      // avoid setting rendered chapterIds if values are the same
      const diff = difference(newChapterIds, renderedChapterIds);
      if (diff.length) setRenderedChapterIds(newChapterIds);
    }
  }, [orderedChapterIds, currentChapterId, renderedChapterIds, chapterIdIndex]);

  const directionedPanelIds =
    scriptDirection === "rtl"
      ? [...renderedChapterIds].reverse()
      : renderedChapterIds;
  const currentPanelId = directionedPanelIds.indexOf(currentChapterId);

  if (!hasChapter) return null;

  return (
    <>
      <Slider
        key={`${directionedPanelIds}`}
        swipeOptions={{
          startSlide: currentPanelId,
          transitionEnd: (index: number) => {
            setModeDefault();
            setChapter({ chapterId: renderedChapterIds[index] });
          },
          continuous: false
        }}
      >
        {directionedPanelIds.map(panelChapterId => (
          <div key={panelChapterId}>
            <ChapterView touchEnabled chapterId={panelChapterId} />
          </div>
        ))}
      </Slider>

      {children}
    </>
  );
};

const MemoizedSwipableChapterLayout = React.memo(SwipableChapterLayout);

interface PerformantSwipableChapterLayoutProps {
  bible: BibleCtx;
  chapter: ChapterCtx;
  theme: Theme;
  setModeDefault: () => void;
}

// NOTE: this coherces data into a format that will take full advantage of React.memo
// which results in much faster transition times when navigating via swipes
const PerformantChapterLayout: React.FC<PerformantSwipableChapterLayoutProps> = ({
  bible,
  chapter,
  theme,
  setModeDefault,
  children
}) => {
  const { bibleId, chapterId, setChapter } = useScriptureRouting();
  const orderedChapterIdsString = (
    bible.biblesById[bibleId]?.orderedChapterIds || []
  ).join(",");
  const memoedSetChapter = useCallback(setChapter, [bibleId]);
  const hasChapter = !!chapter.getChapter();
  const memoedSetModeDefault = useCallback(setModeDefault, []);

  return (
    <MemoizedSwipableChapterLayout
      orderedChapterIdsString={orderedChapterIdsString}
      setChapter={memoedSetChapter}
      currentChapterId={chapterId}
      scriptDirection={theme.scriptDirection}
      hasChapter={hasChapter}
      setModeDefault={memoedSetModeDefault}
    >
      {children}
    </MemoizedSwipableChapterLayout>
  );
};

export default withBible(withChapter(withTheme(PerformantChapterLayout)));
